import React from "react";
import Tooltip from "../../common/Tooltip/Tooltip";
import { COMMANDS } from "../../../utils";
import styles from "./CommandTooltip.module.scss";
import { createPluggable } from "pluggable-react";

import DetailTable from "../../common/DetailTable/DetailTable";
import AttributeValue from "../AttributeValue/AttributeValue";
import Icon from "../../common/Icon/Icon";
import ExpressionErrors from "../ExpressionErrors/ExpressionErrors";

export interface CommandTooltipProps {
    type: string;
    children: React.ReactNode;
    value?: any;
    error?: any;
}

const CommandTooltipContent = ({ type, value, error }: { type: string; value?: any; error?: any }) => {
    const normalizedType = type === "raiseerror" ? "raise-error" : type === "raisewarning" ? "raise-warning" : type;
    const command = COMMANDS.find((c) => c.id === normalizedType);

    const commandDetails = command ? (
        <>
            <div className={styles.title}>{command.displayName || command.id}</div>
            {command.description ? (
                <div dangerouslySetInnerHTML={{ __html: command.description }} />
            ) : (
                <div>No description available for this command.</div>
            )}
        </>
    ) : (
        <>
            <div className={styles.title}>{normalizedType}</div>
            <div>No details available for this command.</div>
        </>
    );

    const hasDebugInfo = value !== undefined || error !== undefined;

    return (
        <div className={styles.container}>
            {commandDetails}
            {hasDebugInfo && (
                <DetailTable className={styles.debugTable}>
                    {value !== undefined && (
                        <DetailTable.Item>
                            <DetailTable.Icon><Icon name="value" /></DetailTable.Icon>
                            <DetailTable.Title>Value</DetailTable.Title>
                            <DetailTable.Content>
                                <AttributeValue node={{ derived: value }} />
                            </DetailTable.Content>
                        </DetailTable.Item>
                    )}
                    {error ? <ExpressionErrors errors={[error]} /> : null}
                </DetailTable>
            )}
        </div>
    );
};

const DefaultCommandTooltip = (props: CommandTooltipProps) => {
    const { type, children, value, error } = props;

    return (
        <Tooltip interactable={true} theme="light">
            <Tooltip.Trigger asChild>
                {children}
            </Tooltip.Trigger>
            <Tooltip.Content className={styles.commandTooltipContent}>
                <CommandTooltipContent type={type} value={value} error={error} />
            </Tooltip.Content>
        </Tooltip>
    );
};

const CommandTooltip = createPluggable<CommandTooltipProps>("deci.commandTooltip", DefaultCommandTooltip);

export default Object.assign(CommandTooltip, {
    Content: CommandTooltipContent
});
